import { useRef, useEffect, useCallback } from "react";

export function useIdleTimers({
    cartItems,
    cartCreatedAtRef,
    cartIdRef,
    analytics,
    navigateToView,
    setIsIdle,
    setHistory,
    setCurrentStep,
    setCartItems,
}) {
    // Riferimenti temporali e di stato
    const lastInteractionTimeRef = useRef(Date.now());
    const intervalRef = useRef(null);
    const stage1TriggeredRef = useRef(false);
    const idleStartedRef = useRef(false);
    const idleStartTimeRef = useRef(null);

    // Costanti di timeout
    const IDLE_TIMEOUT_STAGE_1 = 3 * 60 * 1000;  // 3 minuti
    const IDLE_TIMEOUT_STAGE_2 = 23 * 60 * 1000; // 23 minuti

    // --- PATTERN LATEST REF ---
    // Memorizziamo tutte le props in un ref per accedervi nel setInterval
    // senza dover riavviare l'effetto ogni volta che cambiano.
    const propsRef = useRef({
        cartItems,
        cartCreatedAtRef,
        cartIdRef,
        analytics,
        navigateToView,
        setIsIdle,
        setHistory,
        setCurrentStep,
        setCartItems,
    });

    // Aggiorna il ref ogni volta che una prop cambia
    useEffect(() => {
        propsRef.current = {
            cartItems,
            cartCreatedAtRef,
            cartIdRef,
            analytics,
            navigateToView,
            setIsIdle,
            setHistory,
            setCurrentStep,
            setCartItems,
        };
    }, [
        cartItems,
        cartCreatedAtRef,
        cartIdRef,
        analytics,
        navigateToView,
        setIsIdle,
        setHistory,
        setCurrentStep,
        setCartItems,
    ]);

    // Gestore delle interazioni (stabile grazie a useCallback)
    const handleInteraction = useCallback(async () => {
        const now = Date.now();
        const { analytics: currentAnalytics, setIsIdle: currentSetIsIdle } = propsRef.current;

        // Se l'utente era in idle, termina la sessione di inattività
        if (idleStartedRef.current) {
            const idleStart = idleStartTimeRef.current ?? lastInteractionTimeRef.current;
            const idleDuration = Math.round((now - idleStart) / 1000);

            currentAnalytics.trackIdleEnd(idleDuration);
            currentAnalytics.sessionStart();

            idleStartedRef.current = false;
            idleStartTimeRef.current = null;
        }

        lastInteractionTimeRef.current = now;  // Resetta il timer
        stage1TriggeredRef.current = false;    // Permette di retriggare Stage 1
        currentSetIsIdle(false);               // Chiude il video fullscreen
    }, []);

    useEffect(() => {
        const events = ["click", "keydown", "touchstart"];
        const handleInt = () => handleInteraction();

        events.forEach((ev) => window.addEventListener(ev, handleInt));

        intervalRef.current = setInterval(() => {
            const now = Date.now();
            const elapsed = now - lastInteractionTimeRef.current;

            const {
                cartItems: currentCartItems,
                cartCreatedAtRef: currentCartCreatedAtRef,
                cartIdRef: currentCartIdRef,
                analytics: currentAnalytics,
                navigateToView: currentNavigateToView,
                setIsIdle: currentSetIsIdle,
                setHistory: currentSetHistory,
                setCurrentStep: currentSetCurrentStep,
                setCartItems: currentSetCartItems,
            } = propsRef.current;

            // -------- STAGE 2: Idle completo (Fullscreen Video) --------
            if (elapsed >= IDLE_TIMEOUT_STAGE_2) {
                const disableIdleVideo = new URLSearchParams(window.location.search).get("disableIdleVideo") === "true";
                if (!disableIdleVideo) {
                    currentSetIsIdle(true);
                }
                // ✅ NON fermare l'intervallo, lascia che continui a girare
                return;
            }

            // -------- STAGE 1: Entra in Idle + Reset UI --------
            if (elapsed >= IDLE_TIMEOUT_STAGE_1 && !stage1TriggeredRef.current) {
                stage1TriggeredRef.current = true;

                if (!idleStartedRef.current) {
                    idleStartedRef.current = true;
                    idleStartTimeRef.current = now;  // ✅ Fix
                    currentAnalytics.trackIdleStart();
                }

                // Tracking abbandono carrello
                if (currentCartItems?.length && currentCartCreatedAtRef.current) {
                    const seconds = Math.round(
                        (now - currentCartCreatedAtRef.current) / 1000
                    );

                    currentAnalytics.trackCart("cart_abandoned", {
                        cart_id: currentCartIdRef.current,
                        seconds_since_created: seconds,
                    });

                    currentCartCreatedAtRef.current = null;
                }

                // Reset logico e UI
                currentSetHistory([]);
                currentSetCurrentStep(1);
                currentSetCartItems([]);
                // ⚠️ history=false per evitare di re-inserire un entry stale
                // dopo aver appena svuotato la history
                currentNavigateToView("categories", null, false);

                console.log("[IDLE] Reset completo. URL:", window.location.search);
            }
        }, 1000);

        return () => {
            events.forEach((ev) => window.removeEventListener(ev, handleInt));
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [handleInteraction]);

    return null;
}